import { useEffect, useMemo, useState } from "react";
import { X } from "lucide-react";
import { Input } from "./ui/input";
import { cn } from "@/lib/utils";
import {
  isSafariBrowser,
  normalizeIsoDateValue,
  isoToItalianDisplay,
  italianDisplayToIso,
} from "../utils/dateFieldUtils";

/**
 * Campo data: su Safari usa testo gg/mm/aaaa (vuoto se assente, cancellabile);
 * altri browser usano input type="date" nativo.
 */
export default function DateFieldInput({
  value,
  defaultValue,
  onChange,
  className,
  id,
  disabled,
  placeholder = "gg/mm/aaaa",
  clearable = true,
}) {
  const safari = useMemo(() => isSafariBrowser(), []);
  const isoFromProps = normalizeIsoDateValue(
    value !== undefined ? value : defaultValue
  );

  if (!safari) {
    const common = {
      id,
      disabled,
      className: cn(className),
      onChange: (e) => onChange(e.target.value),
    };
    if (value !== undefined) {
      return <Input type="date" value={isoFromProps} {...common} />;
    }
    return <Input type="date" defaultValue={isoFromProps} {...common} />;
  }

  return (
    <SafariDateTextInput
      isoFromProps={isoFromProps}
      onChange={onChange}
      className={className}
      id={id}
      disabled={disabled}
      placeholder={placeholder}
      clearable={clearable}
    />
  );
}

function SafariDateTextInput({
  isoFromProps,
  onChange,
  className,
  id,
  disabled,
  placeholder,
  clearable,
}) {
  const [display, setDisplay] = useState(() => isoToItalianDisplay(isoFromProps));

  useEffect(() => {
    setDisplay(isoToItalianDisplay(isoFromProps));
  }, [isoFromProps]);

  const commitIso = (iso) => {
    onChange(iso);
    setDisplay(isoToItalianDisplay(iso));
  };

  const handleChange = (e) => {
    const raw = e.target.value;
    setDisplay(raw);
    if (!raw.trim()) {
      onChange("");
    }
  };

  const handleBlur = () => {
    if (!display.trim()) {
      onChange("");
      setDisplay("");
      return;
    }
    const iso = italianDisplayToIso(display);
    if (iso === null) {
      setDisplay(isoToItalianDisplay(isoFromProps));
      return;
    }
    commitIso(iso);
  };

  const handleClear = () => {
    onChange("");
    setDisplay("");
  };

  return (
    <div className={cn("relative flex items-center w-full", className)}>
      <Input
        type="text"
        id={id}
        inputMode="numeric"
        autoComplete="off"
        placeholder={placeholder}
        value={display}
        onChange={handleChange}
        onBlur={handleBlur}
        disabled={disabled}
        className={cn(clearable && display ? "pr-9" : undefined)}
      />
      {clearable && display && !disabled ? (
        <button
          type="button"
          onClick={handleClear}
          className="absolute right-1.5 top-1/2 -translate-y-1/2 p-1 rounded-md text-slate-400 hover:text-slate-700 hover:bg-slate-100"
          title="Cancella data"
          aria-label="Cancella data"
          tabIndex={-1}
        >
          <X className="w-4 h-4" />
        </button>
      ) : null}
    </div>
  );
}
